import Paper from "@mui/material/Paper";
import PaymentInstallment from "components/PaymentInstallment/PaymentInstallment";
import { calculateClassesAmount } from "helper/Calculate";
import { numberFormat } from "helper/numberFormat";
import FormService from "Lib/API/Forms";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAccessTokenSilently, getAuthenticated } from "utils/functions";
import {
  calculateBulkInstallmentAmount,
  calculateInstallmentAmount,
} from "utils/installment";
import {
  DataFoundWrapper,
  Discount,
  Note,
  PayDetails,
  PaymentAmount,
  SummaryEnrollment,
  SummaryEnrollmentAmount,
  SummaryItem,
  SummaryItemCharge,
  SummaryItemInfo,
  SummaryItemLedger,
  SummaryItemName,
  SummaryItemProduct,
  SummaryTicketList,
  SummaryTitle,
  TotalDueAmount,
  TotalPayment,
  Wrapper,
} from "./Theme";
import { Fonts, FontColors } from "theme/fontConfig";

const SummaryCard = ({
  isInstallmentEnabled,
  onUpdatedClassDetails,
  setActivePaymentAfter,
  selectedClassesList,
  allProducts,
  updatedProducts,
  billMyAccount = false,
  dueAmountCF = 0,
  totalPaymentWithCVFee = 0,
  totalDueAmountWithCF = 0,
  isAuth,
  activePayment,
  setConvenienceFeeByPayment,
  convenienceFeeByPayment,
  currentPaymentMethod,
  formDataReservation,
  onCalcDueAmountChange,
  onCalcPriceStatusChange,
  donationAmount,
}) => {
  const isAuthenticated = getAuthenticated();
  const { enqueueSnackbar } = useSnackbar();
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [fetchingDiscount, setFetchingDiscount] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);
  const [apiDueAmount, setApiDueAmount] = useState(null);
  const [apiConvenienceFee, setApiConvenienceFee] = useState(null);
  const [apiTotalDiscount, setApiTotalDiscount] = useState(null);
  const [apiTotal, setApiTotal] = useState(null);
  const triggerRefetch = () => setRefetchTrigger((t) => t + 1);

  const spinStyle = { animation: "spin 1s linear infinite" };
  if (typeof document !== "undefined" && !document.getElementById("__bulk-school-spin-style")) {
    const s = document.createElement("style");
    s.id = "__bulk-school-spin-style";
    s.textContent = "@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }";
    document.head.appendChild(s);
  }

  useEffect(() => {
    let cancelled = false;
    setApiDueAmount(null);
    setApiConvenienceFee(null);
    setApiTotalDiscount(null);
    setApiTotal(null);
    setFetchingDiscount(true);
    setApiFailed(false);
    const fetchCalcPrice = async () => {
      if (!selectedClassesList?.length) return;
      try {
        const token =
          (await getAccessTokenSilently()) ||
          localStorage.getItem("schoolToken") ||
          localStorage.getItem("formToken");
        if (!token || cancelled) return;

        const installments = [];
        selectedClassesList.forEach((classItem) => {
          const prices = (classItem?.class_pricing?.prices) || [];
          prices.forEach((p) => {
            if (p?.installmentStatus && p?.installment?.value) {
              installments.push({ priceId: p._id, numberOfInstallment: p.installment.value });
            }
          });
        });

        const buildProducts = (ticketProducts = []) =>
          ticketProducts
            .filter((p) => p && p.totalCount > 0)
            .map((p) => {
              const obj = { id: p._id, title: p.title, quantity: p.totalCount };
              if (p.variant && p.variantInfo && p.variantInfo.length > 0) {
                const variant = {};
                p.variantInfo.forEach((v) => {
                  if (v.counterVariant > 0) variant[v.name] = v.counterVariant;
                });
                if (Object.keys(variant).length > 0) obj.variant = variant;
              }
              if (p.installmentStatus && p.installment?.value) {
                obj.numberOfInstallments = p.installment.value;
              }
              return obj;
            });

        const registrationForm = selectedClassesList.map((classItem) => {
          const ticketData = (formDataReservation || []).find(
            (fd) => fd.id === classItem?.ticketId,
          );
          return {
            classId: classItem?._id,
            products: buildProducts(ticketData?.products),
          };
        });

        const body = {
          registrationForm,
          paymentMethodId: currentPaymentMethod?._id,
          coverFee: convenienceFeeByPayment?.[activePayment] ?? true,
          isMember: isAuthenticated,
          installments,
          checkoutDonation: { amount: donationAmount || 0 },
        };

        const res = await FormService.calculateSchoolPriceBulk(token, body);
        if (!cancelled) {
          const result = res?.result ?? res;
          onCalcPriceStatusChange && onCalcPriceStatusChange(false);
          onCalcDueAmountChange && onCalcDueAmountChange(result?.dueAmount);
          setApiDueAmount(result?.dueAmount ?? null);
          setApiConvenienceFee(result?.convenienceFee ?? null);
          setApiTotalDiscount(result?.totalDiscount ?? null);
          setApiTotal(result?.totalAmount ?? null);
          setFetchingDiscount(false);
          setApiFailed(false);
        }
      } catch (e) {
        if (!cancelled) {
          onCalcPriceStatusChange && onCalcPriceStatusChange(true);
          setFetchingDiscount(false);
          setApiFailed(true);
          enqueueSnackbar("Failed to fetch prices", { variant: "error" });
        }
      }
    };
    fetchCalcPrice();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPaymentWithCVFee, refetchTrigger]);

  const getClassTotalAmountDiscount = (classItem) => {
    return calculateClassesAmount(
      classItem,
      isAuth,
      selectedClassesList?.length,
    );
  };
  const shouldShowConvenienceFeeToggle =
    currentPaymentMethod?.coverConvenienceFee;

  const isConvenienceFeeOptional =
    currentPaymentMethod?.convenienceFeeInfo?.optional === true;

  return (
    <>
      <Paper
        sx={{
          minHeight: "278px",
          borderRadius: "10px",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#1A2027" : "#fff",
        }}
      >
        <Wrapper>
          <SummaryTitle>
            <span>Summary</span>
          </SummaryTitle>
          <DataFoundWrapper>
            {selectedClassesList?.map((classItem, index) => {
              const {
                totalAmountAll,
                totalDiscountAmount,
              } = getClassTotalAmountDiscount(classItem);
              const rawTotal = classItem?.class_pricing?.prices
                ?.filter((p) => p.type === "onRegistration")
                ?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
              if (rawTotal <= 0) return null;
              return (
                <SummaryTicketList key={classItem?._id}>
                  <SummaryEnrollment>Enrollment #{index + 1}</SummaryEnrollment>
                  {classItem?.class_pricing?.prices?.map(
                    (item, index) =>
                      item?.type === "onRegistration" && (
                        <SummaryItem>
                          <SummaryItemCharge>
                            <SummaryItemInfo>
                              <SummaryItemLedger>
                                {item?.title}
                              </SummaryItemLedger>
                            </SummaryItemInfo>
                            <PaymentAmount>{`${numberFormat({
                              number: +item?.amount,
                              decimal: 2,
                              style: "currency",
                            })}`}</PaymentAmount>
                          </SummaryItemCharge>
                          {isInstallmentEnabled &&
                            item?.isInstallmentTransaction && (
                              <PaymentInstallment
                                onAllowInstallmentChange={(e) => {
                                  const { checked } = e.target;
                                  const updatedClassPricing = classItem?.class_pricing?.prices?.map(
                                    (val) => {
                                      if (val?._id === item?._id) {
                                        return {
                                          ...val,
                                          installmentStatus: checked,
                                          installment: null,
                                        };
                                      }
                                      return val;
                                    },
                                  );
                                  const updatedClassDetails = {
                                    ...classItem,
                                    class_pricing: {
                                      ...classItem?.class_pricing,
                                      prices: updatedClassPricing,
                                    },
                                  };
                                  onUpdatedClassDetails(updatedClassDetails);
                                  setActivePaymentAfter();
                                }}
                                installmentValue={item?.installmentStatus}
                                onSelectInstallment={(value) => {
                                  const updatedClassPricing = classItem?.class_pricing?.prices?.map(
                                    (val) => {
                                      if (val?._id === item?._id) {
                                        return {
                                          ...val,
                                          installment: value,
                                        };
                                      }
                                      return val;
                                    },
                                  );
                                  const updatedClassDetails = {
                                    ...classItem,
                                    class_pricing: {
                                      ...classItem?.class_pricing,
                                      prices: updatedClassPricing,
                                    },
                                  };
                                  onUpdatedClassDetails(updatedClassDetails);
                                  triggerRefetch();
                                }}
                                selectedInstallment={item?.installment}
                                maxInstallment={
                                  item?.installmentDetailsTransaction
                                    ?.numberOfInstallment
                                }
                              />
                            )}
                        </SummaryItem>
                      ),
                  )}
                </SummaryTicketList>
              );
            })}

            {allProducts.map((product, index) => (
              <SummaryItem key={product?._id}>
                {product?.totalCount > 0 && product?.totalAmount > 0 && (
                  <SummaryItemProduct>
                    <SummaryItemCharge>
                      <SummaryItemInfo>
                        <SummaryItemLedger>
                          {product?.totalCount} {product?.title}
                        </SummaryItemLedger>
                      </SummaryItemInfo>
                      {`${numberFormat({
                        number: product?.totalAmount,
                        decimal: 2,
                        style: "currency",
                      })}`}
                    </SummaryItemCharge>
                    {isInstallmentEnabled && product.isInstallmentTransaction && (
                      <PaymentInstallment
                        onAllowInstallmentChange={(e) => {
                          const { checked } = e.target;
                          updatedProducts({
                            productId: product?._id,
                            installmentStatus: checked,
                            installment: !checked ? null : product?.installment,
                          });
                          setActivePaymentAfter();
                        }}
                        installmentValue={product?.installmentStatus || false}
                        onSelectInstallment={(value) => {
                          updatedProducts({
                            productId: product?._id,
                            installmentStatus: product?.installmentStatus,
                            installment: value,
                          });
                          triggerRefetch();
                        }}
                        selectedInstallment={product?.installment}
                        installmentAmount={calculateInstallmentAmount(
                          product,
                          "product",
                        )}
                        maxInstallment={
                          product?.installmentDetailsTransaction
                            ?.numberOfInstallment
                        }
                      />
                    )}
                  </SummaryItemProduct>
                )}
              </SummaryItem>
            ))}

            {shouldShowConvenienceFeeToggle && !billMyAccount && (
              <SummaryItem>
                {/* <SummaryItemCharge>
                  <div>
                    <span style={{ color: "#002E5C", fontSize: "14px" }}>Convenience fee</span>
                  </div>
                  <PaymentAmount>{numberFormat({ number: +dueAmountCF, decimal: 2, style: "currency" })}</PaymentAmount>
                </SummaryItemCharge> */}
                {/* {isConvenienceFeeOptional && (
                  <div
                    className={`wrapper check-box__wrapper`}
                    style={{
                      display: "flex",
                      marginBottom: "10px",
                      marginTop: "10px",
                      color: "#002e5c",
                    }}
                  >
                    <label className="check-box-container">
                      <input
                        type="checkbox"
                        style={{ marginRight: 8 }}
                        checked={convenienceFeeByPayment[activePayment]}
                        onChange={(e) => {
                          setConvenienceFeeByPayment((prev) => ({
                            ...prev,
                            [activePayment]: e.target.checked,
                          }));
                        }}
                      />
                      <span className={"checkbox-checkmark"}></span>
                    </label>
                    <span className="text">
                      {currentPaymentMethod?.convenienceFeeInfo?.message}
                    </span>
                  </div>
                )} */}
                {convenienceFeeByPayment[activePayment] && (
                  <SummaryItemCharge>
                    <div>
                      <span style={{ color: FontColors.Blue002E5C, fontSize: Fonts.GeneralAmountFont.fontSize }}>
                        Convenience fee{" "}
                      </span>
                    </div>
                    <PaymentAmount>
                      {fetchingDiscount ? (
                        <svg style={spinStyle} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                      ) : apiFailed ? (
                        <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "3px 8px", cursor: "pointer" }}>↻ Retry</button>
                      ) : numberFormat({
                        number: apiConvenienceFee !== null ? +apiConvenienceFee : +dueAmountCF,
                        decimal: 2,
                        style: "currency",
                      })}
                    </PaymentAmount>
                  </SummaryItemCharge>
                )}
       {isConvenienceFeeOptional && (
                    <div
                      className={`wrapper check-box__wrapper`}
                      style={{
                        display: "flex",
                        // marginBottom: "10px",
                        marginTop: "10px",
                        paddingTop:"10px",
                        color: FontColors.Slate6B7280,
                        borderTop: "1px dashed rgba(0, 46, 92, 0.10)"

                      }}
                    >
                      <label className="check-box-container" style={{position:"relative"}}>
                        <input
                          type="checkbox"
                          style={{ marginRight: 8 }}
                          checked={convenienceFeeByPayment[activePayment]}
                          onChange={(e) => {
                            setConvenienceFeeByPayment((prev) => ({
                              ...prev,
                              [activePayment]: e.target.checked,
                            }));
                          }}

                          // disabled={disabled}
                          // {...rest}
                        />
                        <span className={"checkbox-checkmark"}></span>
                      </label>
                      <span className="text" style={{paddingLeft:'30px'}}>
                        {currentPaymentMethod?.convenienceFeeInfo?.message}
                      </span>
                    </div>
                  )}

              </SummaryItem>
            )}

            {(fetchingDiscount || apiTotalDiscount !== null ? (apiTotalDiscount > 0) : false) && (
              <PayDetails>
                <li>
                  Total Discount
                  <span>
                    {fetchingDiscount ? (
                      <svg style={spinStyle} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                    ) : apiFailed ? (
                      <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "3px 8px", cursor: "pointer" }}>↻ Retry</button>
                    ) : numberFormat({
                      number: +apiTotalDiscount,
                      decimal: 2,
                      style: "currency",
                    })}
                  </span>
                </li>
              </PayDetails>
            )}
            <TotalPayment>
              <p>Payment Total</p>
              <span>
                {fetchingDiscount ? (
                  <svg style={spinStyle} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                ) : apiFailed ? (
                  <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "3px 8px", cursor: "pointer" }}>↻ Retry</button>
                ) : numberFormat({
                  number: apiTotal !== null ? +apiTotal : +totalPaymentWithCVFee,
                  decimal: 2,
                  style: "currency",
                })}
              </span>
            </TotalPayment>
            <TotalDueAmount>
              <p>{"Due Now"}</p>
              <span>
                {fetchingDiscount ? (
                  <svg style={spinStyle} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                ) : apiFailed ? (
                  <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "3px 8px", cursor: "pointer" }}>↻ Retry</button>
                ) : numberFormat({
                  number: apiDueAmount !== null ? +apiDueAmount : +totalDueAmountWithCF,
                  decimal: 2,
                  style: "currency",
                })}
              </span>
            </TotalDueAmount>
          </DataFoundWrapper>
        </Wrapper>
      </Paper>
    </>
  );
};

export default SummaryCard;
